import DOMPurify from 'dompurify';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import {
    ExpressionInputType,
    ExpressionSpecification,
    LayerSpecification,
    Map as MaplibreMap,
    MapLayerMouseEvent,
    Popup
} from 'maplibre-gl';
import { FeMapPlugin } from '../plugin/fe-map-plugin';
import { FeatureCollectionWithProperties } from './models';

const DEFAULT_SOURCE_PREFIX = 'fe-custom';
const DEFAULT_SLOT_ID = 'fe-user-geojson-slot';

const ELEVATION_PROPERTY_KEY = '__fe-symbol-elevation-meters';

export const CustomGeojsonDefaults = {
    // --- Point (symbol layer) ---
    'icon-size': 1.0,
    'icon-opacity': 1.0,
    'icon-color': '#181C1E',

    // --- Line (line layer) ---
    'line-color': '#007cbf',
    'line-width': 2,
    'line-opacity': 1,

    // --- Polygon (fill layer) ---
    'fill-color': '#007cbf',
    'fill-opacity': 0.5,

    // --- Text (symbol layer, any geometry type) ---
    'text-color': '#000000',
    'text-size': 12,
    'text-anchor': 'center',
    'text-offset': [0, 1.6],
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5
};

export class CustomGeojson implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private slotId?: string;
    private readonly defaults = CustomGeojsonDefaults;
    private readonly tooltip: Popup;
    private readonly trackedLayerIds = new Set<string>();
    private readonly mousemoveHandlers = new Map<string, (e: MapLayerMouseEvent) => void>();
    private readonly mouseleaveHandlers = new Map<string, () => void>();

    // Optional slot layer id. Custom layers will be added *before* this layer.
    constructor(layerSlot?: string) {
        this.slotId = layerSlot ?? DEFAULT_SLOT_ID;
        this.tooltip = new Popup({
            closeButton: false,
            closeOnClick: false,
            maxWidth: '320px'
        });
    }

    onAdd = (map: MaplibreMap) => {
        this.map = map;

        if (!this.slotId || !this.map.getLayer(this.slotId)) {
            console.warn(
                'CustomGeojson plugin: Slot layer with id ' +
                    this.slotId +
                    ' not found. Custom layers will be added on top of the layer stack.'
            );
            this.slotId = undefined;
        }
    };

    onRemove = () => {
        this.removeAllTooltipListeners();
        this.tooltip.remove();
        this.map = null;
    };

    private addTooltipListeners = (layerId: string) => {
        if (!this.map || this.trackedLayerIds.has(layerId)) {
            return;
        }

        const onMouseMove = (e: MapLayerMouseEvent) => {
            if (!this.map) {
                return;
            }
            const feature = e.features?.[0];
            const description = feature?.properties?.['description'];
            if (!description) {
                this.tooltip.remove();
                return;
            }
            this.map.getCanvas().style.cursor = 'pointer';
            this.tooltip
                .setLngLat(e.lngLat)
                .setHTML(DOMPurify.sanitize(String(description)))
                .addTo(this.map);
        };

        const onMouseLeave = () => {
            if (!this.map) {
                return;
            }
            this.map.getCanvas().style.cursor = '';
            this.tooltip.remove();
        };

        this.map.on('mousemove', layerId, onMouseMove);
        this.map.on('mouseleave', layerId, onMouseLeave);
        this.mousemoveHandlers.set(layerId, onMouseMove);
        this.mouseleaveHandlers.set(layerId, onMouseLeave);
        this.trackedLayerIds.add(layerId);
    };

    private removeTooltipListeners = (layerId: string) => {
        if (!this.map) {
            return;
        }
        const onMouseMove = this.mousemoveHandlers.get(layerId);
        const onMouseLeave = this.mouseleaveHandlers.get(layerId);
        if (onMouseMove) {
            this.map.off('mousemove', layerId, onMouseMove);
            this.mousemoveHandlers.delete(layerId);
        }
        if (onMouseLeave) {
            this.map.off('mouseleave', layerId, onMouseLeave);
            this.mouseleaveHandlers.delete(layerId);
        }
        this.trackedLayerIds.delete(layerId);
    };

    private removeAllTooltipListeners = () => {
        for (const layerId of Array.from(this.trackedLayerIds)) {
            this.removeTooltipListeners(layerId);
        }
    };

    addGeojson = (sourceId: string, geojson: FeatureCollectionWithProperties) => {
        if (!this.map) {
            return;
        }

        this.addSource(sourceId, geojson);
        this.addLayers(sourceId, geojson);
    };

    removeGeojson = (sourceId: string) => {
        if (!this.map) {
            return;
        }
        const fullSourceId = this.getSourceId(sourceId);
        ['point', 'line', 'polygon', 'point-symbol', 'line-symbol', 'polygon-symbol'].forEach((suffix) => {
            const layerId = `${fullSourceId}-${suffix}-layer`;
            this.removeTooltipListeners(layerId);
            if (this.map?.getLayer(layerId)) {
                this.map.removeLayer(layerId);
            }
        });
        if (this.map.getSource(fullSourceId)) {
            this.map.removeSource(fullSourceId);
        }
    };

    private addSource = (sourceId: string, geojson: FeatureCollectionWithProperties) => {
        if (!this.map) {
            return;
        }
        const fullSourceId = this.getSourceId(sourceId);
        if (this.map.getSource(fullSourceId)) {
            console.warn(`Source with id ${sourceId} already exists. It will be removed and replaced with new data.`);
            this.map.removeSource(fullSourceId);
        }

        const strictGeojson: FeatureCollection = {
            type: geojson.type,
            features: geojson.features.map(this.withSymbolElevationProperty)
        };
        this.map.addSource(fullSourceId, {
            type: 'geojson',
            data: strictGeojson as FeatureCollection
        });
    };

    private withSymbolElevationProperty = (feature: Feature): Feature => {
        const elevation = this.getPointElevationMeters(feature.geometry);
        if (elevation === null) {
            return feature;
        }
        return { ...feature, properties: { ...feature.properties, [ELEVATION_PROPERTY_KEY]: elevation } };
    };

    private getPointElevationMeters = (geometry: Geometry | null | undefined): number | null => {
        if (!geometry) {
            return null;
        }
        if (geometry.type === 'Point') {
            const z = geometry.coordinates[2];
            return typeof z === 'number' && Number.isFinite(z) ? z : null;
        }
        if (geometry.type === 'MultiPoint') {
            for (const position of geometry.coordinates) {
                const z = position[2];
                if (typeof z === 'number' && Number.isFinite(z)) {
                    return z;
                }
            }
        }
        return null;
    };

    private addLayers = (sourceId: string, geojson: FeatureCollectionWithProperties) => {
        if (!this.map) {
            return;
        }
        const collectionProperties = geojson.properties ?? {};
        const types = this.getUniqueGeometryTypes(geojson);
        const orderedTypes = ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString', 'Point', 'MultiPoint'].filter((x) =>
            types.includes(x)
        );
        orderedTypes.forEach((type) => {
            const config = this.buildLayerConfig(sourceId, type, collectionProperties);
            if (config) {
                this.map?.addLayer(config, this.slotId);
                this.addTooltipListeners(config.id);
            }

            // point layers are symbols so they don't need a separate symbol layer
            if (type === 'Point' || type === 'MultiPoint') {
                return;
            }

            const symbolConfig = this.buildSymbolLayerConfig(sourceId, type, collectionProperties);
            this.map?.addLayer(symbolConfig, this.slotId);
        });
    };

    private getUniqueGeometryTypes = (geojson: FeatureCollectionWithProperties): string[] => {
        const geometryTypes = new Set<string>();
        geojson.features.forEach((feature) => {
            if (feature.geometry) {
                geometryTypes.add(feature.geometry.type);
            }
        });
        return Array.from(geometryTypes);
    };

    private buildZoomFilter = (collectionProperties: Record<string, ExpressionInputType>): ExpressionSpecification => {
        return [
            'all',
            ['>=', ['zoom'], ['to-number', ['coalesce', ['get', 'minzoom'], collectionProperties['minzoom'] ?? 0]]],
            ['<=', ['zoom'], ['to-number', ['coalesce', ['get', 'maxzoom'], collectionProperties['maxzoom'] ?? 24]]]
        ] as ExpressionSpecification;
    };

    private buildLayerConfig = (
        sourceId: string,
        geometryType: string,
        collectionProperties: Record<string, ExpressionInputType>
    ): LayerSpecification | null => {
        const baseLayer = { source: this.getSourceId(sourceId) };
        const zoomFilter = this.buildZoomFilter(collectionProperties);

        if (geometryType === 'Point' || geometryType === 'MultiPoint') {
            return {
                ...baseLayer,
                id: `${baseLayer.source}-point-layer`,
                type: 'symbol',
                filter: ['all', ['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]], zoomFilter] as ExpressionSpecification,
                layout: {
                    'icon-image': [
                        'coalesce',
                        ['image', ['get', 'icon-image']],
                        ['image', collectionProperties['icon-image'] ?? ''],
                        ''
                    ] as ExpressionSpecification,
                    'icon-size': [
                        'coalesce',
                        ['get', 'icon-size'],
                        collectionProperties['icon-size'] ?? this.defaults['icon-size']
                    ] as ExpressionSpecification,

                    'text-field': ['get', 'text-field'],
                    'text-size': [
                        'coalesce',
                        ['get', 'text-size'],
                        collectionProperties['text-size'] ?? this.defaults['text-size']
                    ] as ExpressionSpecification,
                    'text-anchor': [
                        'coalesce',
                        ['get', 'text-anchor'],
                        collectionProperties['text-anchor'] ?? this.defaults['text-anchor']
                    ] as ExpressionSpecification,
                    'text-offset': [
                        'case',
                        ['has', 'text-offset'],
                        ['array', 'number', 2, ['get', 'text-offset']],
                        ['literal', collectionProperties['text-offset'] ?? this.defaults['text-offset']]
                    ] as ExpressionSpecification,
                    'text-allow-overlap': false,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,

                    'symbol-height-offset': ['coalesce', ['get', ELEVATION_PROPERTY_KEY], 0] satisfies ExpressionSpecification,
                    'symbol-height-anchor': 'absolute'
                },
                paint: {
                    'icon-opacity': [
                        'coalesce',
                        ['get', 'icon-opacity'],
                        collectionProperties['icon-opacity'] ?? this.defaults['icon-opacity']
                    ] as ExpressionSpecification,
                    'icon-color': [
                        'coalesce',
                        ['get', 'icon-color'],
                        collectionProperties['icon-color'] ?? this.defaults['icon-color']
                    ] as ExpressionSpecification,

                    'text-color': [
                        'coalesce',
                        ['get', 'text-color'],
                        collectionProperties['text-color'] ?? this.defaults['text-color']
                    ] as ExpressionSpecification,
                    'text-halo-color': [
                        'coalesce',
                        ['get', 'text-halo-color'],
                        collectionProperties['text-halo-color'] ?? this.defaults['text-halo-color']
                    ] as ExpressionSpecification,
                    'text-halo-width': [
                        'coalesce',
                        ['get', 'text-halo-width'],
                        collectionProperties['text-halo-width'] ?? this.defaults['text-halo-width']
                    ] as ExpressionSpecification
                }
            } as LayerSpecification;
        }

        if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
            return {
                ...baseLayer,
                id: `${baseLayer.source}-line-layer`,
                type: 'line',
                filter: ['all', ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]], zoomFilter] as ExpressionSpecification,
                paint: {
                    'line-color': [
                        'coalesce',
                        ['get', 'line-color'],
                        collectionProperties['line-color'] ?? this.defaults['line-color']
                    ] as ExpressionSpecification,
                    'line-width': [
                        'coalesce',
                        ['get', 'line-width'],
                        collectionProperties['line-width'] ?? this.defaults['line-width']
                    ] as ExpressionSpecification,
                    'line-opacity': [
                        'coalesce',
                        ['get', 'line-opacity'],
                        collectionProperties['line-opacity'] ?? this.defaults['line-opacity']
                    ] as ExpressionSpecification,
                    'line-dasharray': [
                        'case',
                        ['==', ['coalesce', ['get', 'line-dasharray'], collectionProperties['line-dasharray'] ?? null], null],
                        ['literal', [1, 0]],
                        ['coalesce', ['get', 'line-dasharray'], collectionProperties['line-dasharray'] ?? ['literal', [1, 0]]]
                    ] as ExpressionSpecification
                }
            } as LayerSpecification;
        }

        if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
            return {
                ...baseLayer,
                id: `${baseLayer.source}-polygon-layer`,
                type: 'fill',
                filter: ['all', ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]], zoomFilter] as ExpressionSpecification,
                paint: {
                    'fill-color': [
                        'coalesce',
                        ['get', 'fill-color'],
                        collectionProperties['fill-color'] ?? this.defaults['fill-color']
                    ] as ExpressionSpecification,
                    'fill-opacity': [
                        'coalesce',
                        ['get', 'fill-opacity'],
                        collectionProperties['fill-opacity'] ?? this.defaults['fill-opacity']
                    ] as ExpressionSpecification
                }
            } as LayerSpecification;
        }

        return null;
    };

    private buildSymbolLayerConfig = (
        sourceId: string,
        geometryType: string,
        collectionProperties: Record<string, ExpressionInputType>
    ): LayerSpecification => {
        const source = this.getSourceId(sourceId);

        const isLine = geometryType === 'LineString' || geometryType === 'MultiLineString';
        const placement: 'line-center' | 'point' = isLine ? 'line-center' : 'point';

        const geometryFilter = isLine
            ? ['LineString', 'MultiLineString']
            : geometryType === 'Polygon' || geometryType === 'MultiPolygon'
              ? ['Polygon', 'MultiPolygon']
              : ['Point', 'MultiPoint'];

        const geomSuffix = isLine ? 'line' : geometryType === 'Polygon' || geometryType === 'MultiPolygon' ? 'polygon' : 'point';

        return {
            id: `${source}-${geomSuffix}-symbol-layer`,
            source,
            type: 'symbol',
            filter: [
                'all',
                ['has', 'text-field'],
                ['in', ['geometry-type'], ['literal', geometryFilter]],
                this.buildZoomFilter(collectionProperties)
            ] as ExpressionSpecification,
            layout: {
                'symbol-placement': placement,
                'text-field': ['get', 'text-field'],
                'text-size': [
                    'coalesce',
                    ['get', 'text-size'],
                    collectionProperties['text-size'] ?? this.defaults['text-size']
                ] as ExpressionSpecification,
                'text-anchor': [
                    'coalesce',
                    ['get', 'text-anchor'],
                    collectionProperties['text-anchor'] ?? this.defaults['text-anchor']
                ] as ExpressionSpecification,
                'text-offset': [
                    'case',
                    ['has', 'text-offset'],
                    ['array', 'number', 2, ['get', 'text-offset']],
                    ['literal', collectionProperties['text-offset'] ?? this.defaults['text-offset']]
                ] as ExpressionSpecification,
                'text-allow-overlap': false
            },
            paint: {
                'text-color': [
                    'coalesce',
                    ['get', 'text-color'],
                    collectionProperties['text-color'] ?? this.defaults['text-color']
                ] as ExpressionSpecification,
                'text-halo-color': [
                    'coalesce',
                    ['get', 'text-halo-color'],
                    collectionProperties['text-halo-color'] ?? this.defaults['text-halo-color']
                ] as ExpressionSpecification,
                'text-halo-width': [
                    'coalesce',
                    ['get', 'text-halo-width'],
                    collectionProperties['text-halo-width'] ?? this.defaults['text-halo-width']
                ] as ExpressionSpecification
            }
        } as LayerSpecification;
    };

    private getSourceId = (id: string) => {
        return `${DEFAULT_SOURCE_PREFIX}-${id}`;
    };
}
